import { Circle, Text } from 'native-base';
import { Dimensions, TouchableOpacity, View } from 'react-native';
import { ProductGoodsDTO, ProductTypeEnumValue } from '@tw/clvip-api-gen';
import React, { useCallback, useMemo, useState } from 'react';
/*
 * Copyright (c) 2019 3will Corporation and others.
 * All rights reserved. This program and the accompanying materials
 * are made available under the terms of the 3will License v1.0
 * which accompanies this distribution, and is available at
 *
 *     http://www.3will.cn/licenses/license-v1.0
 *
 * Contributors:
 *    3will - initial API and implementation
 */
import {
  TwBadge,
  TwBadgeRibbon,
  TwButton,
  TwColumn,
  TwGridFlatList,
  TwIcon,
  TwPage,
  TwRow,
  TwTag
} from '@tw/tw-components-rn';
import { TwTcCartFooter, TwTcCommodityCardEdit, TwTcOrderNotifyBar } from '@tw/tw-templates-rn';

import { BfAppContext } from '@tw/tw-runtime';
import { CommonStore } from '@/app/core/store/common/common-store';
import { GoodItem } from '../../../component/good-item/GoodItem';
import { MIN_OPEN_PACKAGE_AMOUNT } from '@/app/core/store/common/type';
import { MallFilters } from './MallFilters';
import { MaterialIcons } from '@expo/vector-icons';
import { ROUTE_NAMES } from '@/app/navigation/route-names';
import { TwCart } from '@tw/tw-components-rn';
import { TwSideList } from '@/app/components/tw-side-list';
import { TwTheme } from '@tw/tw-runtime-rn';
import { getGoodTypeColor } from '@/app/utils/utils';
import { styles } from '../styles';
import { useStoreState } from '@tw/tw-runtime-react';

export const MallContent = (props) => {
  const commonStore = CommonStore.getInstance();

  const [store] = useState(commonStore);

  const goods = useStoreState(store, (state) => state.goods);
  const productType = useStoreState(store, (state) => state.productType);
  const productTypeEnum = useStoreState(store, (state) => state.productTypeEnum);
  const openPackageProductTypeEnums = useStoreState(store, (state) => state.openPackageProductTypeEnums);
  const saleCardProductTypeEnums = useStoreState(store, (state) => state.saleCardProductTypeEnums);
  const selectedGoods = useStoreState(store, (state) => state.selectedGoods);
  const underUpperPriceGoods = useStoreState(store, (state) => state.underUpperPriceGoods);
  const selectedGoodsAmount = useStoreState(store, (state) => state.selectedGoodsAmount) ?? 0;
  const welfareAmount = useStoreState(store, (state) => state.welfareAmount);
  const amountDiff = welfareAmount - selectedGoodsAmount;
  const isContainOpenPackage = useStoreState(store, (state) => state.isContainOpenPackage);
  const isContainOpenPackageOk = useStoreState(store, (state) => state.isContainOpenPackageOk);
  const myCurActivityOrder = useStoreState(store, (state) => state.myCurActivityOrder);
  const curActivity = useStoreState(store, (state) => state.curActivity);
  const openPackageAmount = useStoreState(store, (state) => state.openPackageAmount);

  const openPackageAmountDiff = MIN_OPEN_PACKAGE_AMOUNT - openPackageAmount;

  const cartPopupVisible = useStoreState(store, (state) => state.cartPopupVisible);
  const addToCartPopupVisible = useStoreState(store, (state) => state.addToCartPopupVisible);
  const addToCartPopupLoading = useStoreState(store, (state) => state.addToCartPopupLoading);

  const pageNo = useStoreState(store, (state) => state.pageNo);
  const pageSize = useStoreState(store, (state) => state.pageSize);
  const pageTotal = useStoreState(store, (state) => state.pageTotal);
  const canLoadMore = pageNo * pageSize <= pageTotal;

  const isLoading = useStoreState(store, (state) => state.isLoading);
  const openPackageProductType = useStoreState(store, (state) => state.openPackageProductType);
  const saleCardProductType = useStoreState(store, (state) => state.saleCardProductType);

  const getGoodCount = useCallback(
    (good: ProductGoodsDTO) => {
      const existingGoodIndex = selectedGoods?.findIndex((item) => item.good.id === good.id);
      if (existingGoodIndex >= 0) {
        console.log('getGoodCount', selectedGoods[existingGoodIndex].count);
        return selectedGoods[existingGoodIndex].count;
      } else {
        return 0;
      }
    },
    [selectedGoods]
  );

  const curActivityAvailable = useMemo(() => {
    return curActivity.choseDeadline && new Date().getTime() < curActivity.choseDeadline;
  }, [curActivity]);

  const isGoodSelectable = useCallback(
    (good: ProductGoodsDTO) => {
      // 当前活动结束时，不可选
      if (!curActivityAvailable) {
        return false;
      }

      // 有活动订单时，不可选
      if (myCurActivityOrder) {
        return false;
      }

      // 凑满时不可选
      if (amountDiff === 0) {
        return false;
      }

      // 到达上限时不可选
      if (getGoodCount(good) >= good.chooseLimitMax) {
        return true;
      }

      return true;
    },
    [amountDiff, curActivityAvailable, getGoodCount, myCurActivityOrder]
  );

  const getSubmitButtonText = useMemo(() => {
    if (!curActivityAvailable) {
      return '活动已结束';
    } else {
      return '选好了';
    }
  }, [curActivityAvailable]);

  const getLimitAmountContent = useMemo(() => {
    if (isContainOpenPackage && !isContainOpenPackageOk) {
      if (openPackageAmountDiff > 0) {
        return `零售差¥${openPackageAmountDiff}凑满`;
      }
    } else if (amountDiff === 0) {
      return '已凑满';
    } else {
      return undefined;
    }
  }, [amountDiff, isContainOpenPackage, isContainOpenPackageOk, openPackageAmountDiff]);

  const getOrderNotifyBar = useMemo(() => {
    // 零售没凑满就优先零售凑满
    if (!isContainOpenPackageOk) {
      return (
        <Text>
          {getLimitAmountContent} 总额差{amountDiff}凑满
        </Text>
      );
    } else {
      if (amountDiff === 0) {
        return <Text>已凑满</Text>;
      } else {
        return <Text>总额差{amountDiff}凑满</Text>;
      }
    }
  }, [amountDiff, getLimitAmountContent, isContainOpenPackageOk]);

  console.log('getOrderNotifyBar', getOrderNotifyBar);

  const isSubmitButtonPressable = useMemo(() => {
    // 当前活动结束时，不可选
    if (!curActivityAvailable) {
      return false;
    }
    if (isContainOpenPackage && !isContainOpenPackageOk) {
      return false;
    }
    if (selectedGoods?.length > 0 && selectedGoodsAmount === welfareAmount) {
      return true;
    } else {
      return false;
    }
  }, [
    curActivityAvailable,
    isContainOpenPackage,
    isContainOpenPackageOk,
    selectedGoods?.length,
    selectedGoodsAmount,
    welfareAmount
  ]);

  const screenWidth = Dimensions.get('window').width;
  // plus padding
  const contentWidth = screenWidth - 32;

  return (
    <TwPage
      translucentStatusBar={true}
      footer={
        !addToCartPopupVisible ? (
          <TwCart
            // 不理解为什么要加key 控制台报错
            data={selectedGoods}
            isPopupVisible={cartPopupVisible}
            popupTitle="我的订单"
            showClearIcon={true}
            onPopupVisibleChange={(visible) => store.setCartPopUpVisible(visible)}
            count={store.getSelectedGoodCount()}
            enter={
              <TwTcCartFooter
                count={store.getSelectedGoodCount()}
                amount={selectedGoodsAmount}
                limitAmount={amountDiff > 0 ? amountDiff : undefined}
                amountText={'合计'}
                limitAmountContent={getLimitAmountContent}
                submitText={getSubmitButtonText}
                isSubmitPressable={isSubmitButtonPressable}
                onCartPress={() => {
                  store.setCartPopUpVisible(true);
                }}
                onSubmitPress={() => {
                  BfAppContext.getInstance().getRouteService().push({
                    path: ROUTE_NAMES.GiftGiftConfirmOrder
                  });
                }}
              />
            }
            styles={{
              container: {
                paddingHorizontal: 10
              }
            }}
            renderItem={(item) => {
              const good = item.item.good;
              return (
                <View style={styles.goodCard}>
                  <TwTcCommodityCardEdit
                    key={item.index}
                    imageUrl={good?.showPicture?.url}
                    title={good.name}
                    titleLines={1}
                    price={good.price}
                    num={item.item.count}
                    tag={
                      <TwTag alignSelf="center" variant={'solid'} bgColor={getGoodTypeColor(good.type.code)}>
                        {good.type.name}
                      </TwTag>
                    }
                    onNumChange={(num) => {
                      if (num > item.item.count) {
                        if (getGoodCount(item.item.good) >= item.item.good.chooseLimitMax) {
                          BfAppContext.getInstance().getMessageService().showWarn('已达到最大限购数量');
                        } else {
                          store.addGoodToCart(good, 'order');
                        }
                      } else {
                        store.deleteGoodToCart(good, 'order');
                      }
                    }}
                  />
                </View>
              );
            }}
            onClear={async () => {
              await store.clearCart();
              store.setCartPopUpVisible(false);
            }}
            banner={
              amountDiff > 0 ? (
                <TwTcOrderNotifyBar
                  styles={{
                    container: styles.notifyBarContainer
                  }}
                  onPress={() => {
                    store.setAddToCartPopupVisible(true);
                    store.setCartPopUpVisible(false);
                    store.getUnderGoods(amountDiff, undefined, 'requirement');
                  }}
                  left={<Text>{getOrderNotifyBar}</Text>}
                  right={
                    <TwRow>
                      <Text>去加购</Text>
                      <MaterialIcons name={'keyboard-arrow-right'} size={20} color={TwTheme.values.textColors.forth} />
                    </TwRow>
                  }
                />
              ) : null
            }
            footer={
              <View style={styles.cartFooter}>
                <TwTcCartFooter
                  count={store.getSelectedGoodCount()}
                  amount={selectedGoodsAmount}
                  limitAmount={amountDiff > 0 ? amountDiff : undefined}
                  limitAmountContent={getLimitAmountContent}
                  amountText={'合计'}
                  submitText={getSubmitButtonText}
                  isSubmitPressable={isSubmitButtonPressable}
                  onSubmitPress={() => {
                    store.setCartPopUpVisible(false);
                    store.getAddress();
                    BfAppContext.getInstance().getRouteService().push({
                      path: ROUTE_NAMES.GiftGiftConfirmOrder
                    });
                  }}
                />
              </View>
            }
          />
        ) : (
          <TwCart
            // 不理解为什么要加key 控制台报错
            data={underUpperPriceGoods}
            popupTitle="加购"
            buttonText="取消"
            showClearIcon={false}
            isPopupVisible={addToCartPopupVisible}
            onPopupVisibleChange={(visible) => store.setAddToCartPopupVisible(visible)}
            onClear={() => {
              store.setAddToCartPopupVisible(false);
              store.setCartPopUpVisible(true);
            }}
            onConfirm={() => {
              store.setAddToCartPopupVisible(false);
              store.setCartPopUpVisible(true);
            }}
            styles={{
              container: {
                paddingHorizontal: 10
              }
            }}
            loading={addToCartPopupLoading}
            renderItem={(item) => {
              const good = item.item.good;
              return (
                <View style={styles.goodCard}>
                  <TwTcCommodityCardEdit
                    key={item.index}
                    imageUrl={good?.showPicture?.url}
                    title={good.name}
                    titleLines={1}
                    price={good.price}
                    num={item.item.count}
                    tag={
                      <TwTag alignSelf="center" variant={'solid'} bgColor={getGoodTypeColor(good.type.code)}>
                        {good.type.name}
                      </TwTag>
                    }
                    onNumChange={(num) => {
                      if (num > item.item.count) {
                        if (getGoodCount(item.item.good) >= item.item.good.chooseLimitMax) {
                          BfAppContext.getInstance().getMessageService().showWarn('已达到最大限购数量');
                        } else {
                          store.addGoodToCart(good, 'underUpperPrice');
                        }
                      } else {
                        store.deleteGoodToCart(good, 'underUpperPrice');
                      }
                    }}
                  />
                </View>
              );
            }}
            // header={
            //   <View style={styles.addToCartHeader}>
            //     <Text fontSize="md">加购</Text>
            //   </View>
            // }
            footer={
              <TwButton
                size={'sm'}
                style={styles.confirmButton}
                onPress={() => {
                  store.setAddToCartPopupVisible(false);
                  store.setCartPopUpVisible(true);
                }}
              >
                确定
              </TwButton>
            }
            banner={<TwTcOrderNotifyBar left={getOrderNotifyBar} />}
            count={store.getSelectedGoodCount()}
          />
        )
      }
    >
      <View style={styles.container}>
        <TwRow height={'100%'}>
          {/* 零售 */}
          {productType === ProductTypeEnumValue.open_package && (
            <TwSideList
              items={openPackageProductTypeEnums.map((item) => {
                return {
                  id: item.code,
                  name: item.name
                };
              })}
              activeKey={openPackageProductType}
              onActiveItemChange={(item) => {
                store.onOpenPackageProductTypeChange(item.id);
              }}
            />
          )}
          {/* 卡券 */}
          {productType === ProductTypeEnumValue.sale_card && (
            <TwSideList
              items={saleCardProductTypeEnums.map((item) => {
                return {
                  id: item.code,
                  name: item.name
                };
              })}
              activeKey={saleCardProductType}
              onActiveItemChange={(item) => {
                store.onSaleCardProductTypeChange(item.id);
              }}
            />
          )}
          <View style={styles.contentContainer}>
            <TwColumn space={2} flex={1}>
              <MallFilters />
              <TwGridFlatList
                items={goods}
                space={3}
                onEndReached={canLoadMore ? () => store.loadMore() : undefined}
                onEndReachedThreshold={0.5}
                estimatedItemSize={170}
                numColumns={2}
                extraData={selectedGoods}
                renderItem={(item) => {
                  // TODO: 代码太丑了 有空重构
                  return item.item.isAgricultural ? (
                    <TwBadgeRibbon title={'助农产品'} titleBackgroundColor="#53B260" titleColor="#fff">
                      <GoodItem
                        {...item.item}
                        onPress={() => store.onGoodClick(item.item, 'edit')}
                        buttons={
                          <TouchableOpacity
                            onPress={() => {
                              if (!isGoodSelectable(item.item) || getGoodCount(item.item) >= item.item.chooseLimitMax) {
                                return;
                              } else {
                                store.addGoodToCart(item.item, 'mall');
                              }
                            }}
                          >
                            <TwBadge count={getGoodCount(item.item)} offset={[-10, -5]}>
                              <Circle
                                style={[
                                  styles.iconBorder,
                                  { borderColor: isGoodSelectable(item.item) ? '#8FBD69' : '#D7D7D7' }
                                ]}
                              >
                                <TwIcon
                                  icon={'icon-shopping_cart1'}
                                  size={20}
                                  color={isGoodSelectable(item.item) ? '#8FBD69' : '#D7D7D7'}
                                ></TwIcon>
                              </Circle>
                            </TwBadge>
                          </TouchableOpacity>
                        }
                      />
                    </TwBadgeRibbon>
                  ) : (
                    <GoodItem
                      {...item.item}
                      onPress={() => store.onGoodClick(item.item, 'edit')}
                      buttons={
                        <TouchableOpacity
                          onPress={() => {
                            if (!isGoodSelectable(item.item) || getGoodCount(item.item) >= item.item.chooseLimitMax) {
                              return;
                            } else {
                              store.addGoodToCart(item.item, 'mall');
                            }
                          }}
                        >
                          <TwBadge count={getGoodCount(item.item)} offset={[-10, -5]}>
                            <Circle
                              style={[
                                styles.iconBorder,
                                { borderColor: isGoodSelectable(item.item) ? '#8FBD69' : '#D7D7D7' }
                              ]}
                            >
                              <TwIcon
                                icon={'icon-shopping_cart1'}
                                size={20}
                                color={isGoodSelectable(item.item) ? '#8FBD69' : '#D7D7D7'}
                              ></TwIcon>
                            </Circle>
                          </TwBadge>
                        </TouchableOpacity>
                      }
                    />
                  );
                }}
                ListFooterComponent={
                  <View style={{ height: 20, alignItems: 'center' }}>
                    <Text>{isLoading ? '正在加载更多…' : !canLoadMore ? '没有更多数据了' : ''}</Text>
                  </View>
                }
              />
            </TwColumn>
          </View>
        </TwRow>
      </View>
    </TwPage>
  );
};
